import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "col" }
const _hoisted_3 = {
  key: 1,
  class: "table table-sm table-hover"
}
const _hoisted_4 = /*#__PURE__*/_createElementVNode("caption", { class: "d-none" }, "User notification channels", -1)
const _hoisted_5 = { scope: "row" }
const _hoisted_6 = { style: {"width":"30px"} }
const _hoisted_7 = {
  class: "btn-group btn-group-sm",
  role: "group",
  "aria-label": "Notification channel actions"
}
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { colspan: "3" }

import { FormKitSchema } from '@formkit/vue'
import { computed, onBeforeMount, onUnmounted, reactive, ref } from 'vue'
import { isEqual } from 'lodash'
import { createAsyncActionPromise } from '@/helpers/tools'
import { NotificationChannel, NotificationType, Uuid } from '@/types/api'
import { FormKitSchemaNode } from '@formkit/core'
import { userProfilesApi } from '@/di/api'
import AppComponentHeader from "@/components/app/AppComponentHeader.vue";
import AppLoadingMessage from "@/components/app/AppLoadingMessage.vue";
import AppIconLoadingButton from "@/components/app/AppIconLoadingButton.vue";
import eventBus from "@/di/eventBus";
import {
  EVENT_NAME__USER_NOTIFICATION_CHANNEL_CREATED,
  EVENT_NAME__USER_NOTIFICATION_CHANNEL_REMOVED,
  EVENT_NAME__USER_NOTIFICATION_CHANNEL_TYPES_UPDATED
} from "@/api/UserProfilesApi";
import FormKitSubmitButtonContent from "@/components/app/FormKit/FormKitSubmitButtonContent.vue";
import { channelsConfig } from "@/components/customer/notificationChannels/channelsConfig";

interface Lock {
  formSubmitting: {
    addChannel: boolean,
    editChannel: boolean,
  },
  notificationTypesLoading: boolean,
  triggeringTestNotificationId: string | undefined,
  removingId: string | undefined,
  editingId: Uuid | undefined,
  loadingForEditId: string | undefined,
}

interface Data {
  userNotificationChannels: NotificationChannel[],
  allowedNotificationTypes: NotificationType[],
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NotificationChannelsList',
  setup(__props) {

const checkedChannelTypes = (channelTypes: { [key: string]: boolean }): string[] => Object.keys(channelTypes).filter(field => channelTypes[field])

const data = ref<Data>({
  userNotificationChannels: [],
  allowedNotificationTypes: [],
})

const lock = reactive<Lock>({
  formSubmitting: {
    addChannel: false,
    editChannel: false
  },
  notificationTypesLoading: false,
  triggeringTestNotificationId: undefined,
  removingId: undefined,
  editingId: undefined,
  loadingForEditId: undefined
})

const modelEditChannelTypes = ref<{ [key: string]: boolean }>({})

const notificationChannelTypesOriginal = ref<{ [key: string]: boolean }>({})

const modelEditSchema = computed((): FormKitSchemaNode[] => {
  return Object.keys(modelEditChannelTypes.value)
      .map((machineName: string): FormKitSchemaNode => ({
        label: data.value.allowedNotificationTypes.filter((nt) => nt.machine_name === machineName)[0].name,
        name: machineName,
        $formkit: 'checkbox',
        'wrapper-class': 'form-check',
        'element-class': 'd-inline',
        'input-class': 'form-check-input',
        'label-class': 'small'
      }))
})

const editFormSubmitDisabled = computed((): boolean => {
  return isEqual(
          checkedChannelTypes(modelEditChannelTypes.value),
          checkedChannelTypes(notificationChannelTypesOriginal.value)
      )
      || lock.formSubmitting.editChannel
})

async function removeChannel (id: string): Promise<void> {
  if (lock.removingId) return

  if (!confirm('Remove')) return

  lock.removingId = id

  try {
    await userProfilesApi.removeNotificationChannel(id)
  } finally {
    lock.removingId = undefined
  }
}

async function loadUserNotificationChannelTypes (notificationChannelId: string): Promise<void> {
  if (lock.editingId) {
    lock.editingId = undefined
    return
  }

  lock.editingId = undefined
  lock.loadingForEditId = notificationChannelId

  const notificationChannelTypes = await userProfilesApi.getNotificationTypesForNotificationChannel(notificationChannelId)

  const _modelEditChannelTypes: { [key: string]: boolean } = {}
  data.value.allowedNotificationTypes.forEach((type): void => {
    _modelEditChannelTypes[type.machine_name] = notificationChannelTypes.includes(type.machine_name)
  })

  if (modelEditChannelTypes.value === undefined) {
    modelEditChannelTypes.value = {}
  }

  Object.assign(modelEditChannelTypes.value, _modelEditChannelTypes)
  Object.assign(notificationChannelTypesOriginal.value, _modelEditChannelTypes)

  lock.editingId = notificationChannelId
  lock.loadingForEditId = undefined
}

async function updateUserNotificationChannelTypes (): Promise<void> {
  lock.formSubmitting.editChannel = true

  const notificationTypes: string[] = []

  Object.keys(modelEditChannelTypes.value)
      .forEach((machineName: string): void => {
        if (modelEditChannelTypes.value[machineName]) {
          notificationTypes.push(machineName)
        }
      })

  try {
    await createAsyncActionPromise(
        eventBus,
        () => userProfilesApi.updateNotificationTypesForNotificationChannel(lock.editingId as Uuid, notificationTypes),
        () => Promise.resolve(),
        EVENT_NAME__USER_NOTIFICATION_CHANNEL_TYPES_UPDATED
    )
  } finally {
    modelEditChannelTypes.value = {}
    lock.editingId = undefined
    lock.formSubmitting.editChannel = false
    notificationChannelTypesOriginal.value = {}
  }
}

async function triggerTestNotification (notificationChannelId: Uuid): Promise<void> {
  lock.triggeringTestNotificationId = notificationChannelId

  try {
    await userProfilesApi.triggerTestNotification(notificationChannelId)
  } finally {
    lock.triggeringTestNotificationId = undefined
  }
}

function getChannelTitle (type: string): string {
  return channelsConfig.allowedChannels.filter((ch) => ch.name === type)[0].title
}

function isChannelDeletable (type: string): boolean {
  return channelsConfig.allowedChannels.filter((ch) => ch.name === type)[0].deletable
}

const loaders = {
  async loadNotificationChannels (): Promise<void> {
    const notificationChannels = await userProfilesApi.getNotificationChannels()
    data.value.userNotificationChannels = notificationChannels.items
  },
  async loadNotificationTypes (): Promise<void> {
    data.value.allowedNotificationTypes = await userProfilesApi.getNotificationTypes()
  }
}

eventBus.$on(EVENT_NAME__USER_NOTIFICATION_CHANNEL_CREATED, loaders.loadNotificationChannels)
eventBus.$on(EVENT_NAME__USER_NOTIFICATION_CHANNEL_REMOVED, loaders.loadNotificationChannels)

onBeforeMount(async (): Promise<void> => {
  await Promise.all([
    loaders.loadNotificationChannels(),
    loaders.loadNotificationTypes()
  ])
})

onUnmounted(() => {
  eventBus.$off(EVENT_NAME__USER_NOTIFICATION_CHANNEL_CREATED, loaders.loadNotificationChannels)
  eventBus.$off(EVENT_NAME__USER_NOTIFICATION_CHANNEL_REMOVED, loaders.loadNotificationChannels)
})

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(AppComponentHeader, null, {
        default: _withCtx(() => [
          _createTextVNode("Notification channels")
        ]),
        _: 1
      }),
      (data.value.userNotificationChannels.length === 0)
        ? (_openBlock(), _createBlock(AppLoadingMessage, {
            key: 0,
            class: "mb-4"
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Loading notification channels list ")
            ]),
            _: 1
          }))
        : (_openBlock(), _createElementBlock("table", _hoisted_3, [
            _hoisted_4,
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.value.userNotificationChannels, (userNotificationChannel) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: userNotificationChannel.id
              }, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", _hoisted_5, _toDisplayString(getChannelTitle(userNotificationChannel.type)), 1),
                  _createElementVNode("td", null, _toDisplayString(userNotificationChannel.name), 1),
                  _createElementVNode("td", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _createVNode(AppIconLoadingButton, {
                        icon: "bolt",
                        "button-type": "info",
                        loading: lock.triggeringTestNotificationId === userNotificationChannel.id,
                        disabled: !!lock.triggeringTestNotificationId,
                        onClick: ($event: any) => (triggerTestNotification(userNotificationChannel.id))
                      }, null, 8, ["loading", "disabled", "onClick"]),
                      _createVNode(AppIconLoadingButton, {
                        icon: "edit",
                        "button-type": "primary",
                        loading: lock.loadingForEditId === userNotificationChannel.id,
                        disabled: !!lock.loadingForEditId,
                        onClick: ($event: any) => (loadUserNotificationChannelTypes(userNotificationChannel.id))
                      }, null, 8, ["loading", "disabled", "onClick"]),
                      _createVNode(AppIconLoadingButton, {
                        icon: "trash",
                        "button-type": "danger",
                        loading: lock.removingId === userNotificationChannel.id,
                        disabled: !!lock.removingId || !isChannelDeletable(userNotificationChannel.type),
                        onClick: ($event: any) => (removeChannel(userNotificationChannel.id))
                      }, null, 8, ["loading", "disabled", "onClick"])
                    ])
                  ])
                ]),
                (lock.editingId === userNotificationChannel.id)
                  ? (_openBlock(), _createElementBlock("tr", _hoisted_8, [
                      _createElementVNode("td", _hoisted_9, [
                        _createVNode(_component_FormKit, {
                          modelValue: modelEditChannelTypes.value,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modelEditChannelTypes).value = $event)),
                          type: "form",
                          class: "form-edit-channel-types",
                          actions: false,
                          onSubmit: updateUserNotificationChannelTypes
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(FormKitSchema), { schema: _unref(modelEditSchema) }, null, 8, ["schema"]),
                            _createVNode(_component_FormKit, {
                              type: "submit",
                              disabled: _unref(editFormSubmitDisabled),
                              "input-class": "btn btn-sm btn-primary",
                              "outer-class": "mt-1 mb-2"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(FormKitSubmitButtonContent, null, {
                                  default: _withCtx(() => [
                                    _createTextVNode("Save")
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }, 8, ["disabled"])
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ], 64))
            }), 128))
          ]))
    ])
  ]))
}
}

})